import ReactGA from 'react-ga4';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import Image from 'next/image';
import BaseModal from 'components/BaseModal';
import { makeStyles } from '@mui/styles';
import { Box, Typography } from '@mui/material';
import { COLOR_GRAY_70, COLOR_GRAY_90 } from 'styles/variables/colors';
import { Icons } from 'components/Icons';
import CustomButton from 'components/CustomButton';
import { useAuthUser } from 'features/auth/AuthUserContext';

const Props = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export const useStyles = makeStyles((theme) => ({
  listBenefits: {
    padding: 0,

    '& li': {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(0.5, 0),
    },

    '& svg': {
      color: '#55a30b',
      width: 15,
      height: 15,
      marginRight: theme.spacing(1),
    },

    '& span': {
      color: COLOR_GRAY_70,
    },
  },
  icon: {
    color: '#55a30b',
  },
  textBenefits: {
    color: '#636161',
  },
  rootModal: {
    paddingBottom: theme.spacing(5),
  },
  subTitle: {
    color: COLOR_GRAY_70,
    marginTop: theme.spacing(0.8),
  },
  afterMonthText: {
    color: COLOR_GRAY_70,
    fontSize: 12,
    textAlign: 'left',
  },
  trustedTitle: {
    color: COLOR_GRAY_90,
    fontSize: 21,
  },
}));

export default function BlackNovemberLimitModal({ open, onClose }) {
  const { user } = useAuthUser();
  const classes = useStyles();

  function sendOpenTrackingEvents() {
    ReactGA.event({
      category: 'my_creations',
      action: 'blacknovember_limit_banner_presented',
      label: 'My Creations Banner Black November Limit Presented',
      value: user.id,
    });
  }

  function sendClickTrackingEvents() {
    ReactGA.event({
      category: 'my_creations',
      action: 'blacknovember_banner_limit_clicked',
      label: 'My Creations Black November Banner Limit Clicked',
      value: user.id,
    });
  }

  function handleActionClick() {
    sendClickTrackingEvents();

    window.open(`/pricing?promotion=black_november`);
  }

  useEffect(() => {
    if (open) {
      sendOpenTrackingEvents();
    }
  }, [open]);

  return (
    <BaseModal
      classes={{ paper: classes.rootModal }}
      open={open}
      title={
        <b>
          Get an upgrade for 1 USD{' '}
          <Typography className={classes.subTitle}>
            Join Researcher Plan for a month paying only US$1*.
          </Typography>
        </b>
      }
      size="md"
      fullWidth={false}
      content={
        <Box>
          <Box display="flex" justifyContent="center">
            <Box>
              <Image
                src="/images/black-november-2023-modal-workspace.svg"
                width={302}
                height={332}
                alt="black november image"
              />

              <Typography className={classes.afterMonthText}>
                * After the first month you will be charged the regular price of <br /> 25 USD
                monthly.
              </Typography>
            </Box>

            <Box textAlign="left" pl={6}>
              <Typography style={{ color: COLOR_GRAY_90 }} component="p" variant="h5">
                <b>Upgrade and enjoy:</b>
              </Typography>

              <ul className={classes.listBenefits}>
                <li>
                  <Icons.checkCircle /> <span>No more watermarks</span>
                </li>
                <li>
                  <Icons.checkCircle /> <span>Unlimited scientific illustrations</span>
                </li>
                <li>
                  <Icons.checkCircle /> <span>5 custom illustrations requests per month</span>
                </li>
                <li>
                  <Icons.checkCircle /> <span>High definition downloads (PNG, PDF and TIF)</span>
                </li>
                <li>
                  <Icons.checkCircle /> <span>Copyrights to publish</span>
                </li>
                <li>
                  <Icons.checkCircle /> <span>Priority customer support</span>
                </li>
                <li>
                  <Icons.checkCircle /> <span>And more...</span>
                </li>
              </ul>

              <CustomButton onClick={handleActionClick}>Upgrade My Subscription</CustomButton>
            </Box>
          </Box>

          <Box pt={4}>
            <Typography className={classes.trustedTitle}>
              Trusted by researchers and academics from 100+ institutions:
            </Typography>

            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap="30px"
              pt={3}
              mb={-2}
            >
              <div>
                <Image
                  src="/images/stanford-logo.webp"
                  width={89}
                  height={40}
                  alt="stanford logo"
                  loading="eager"
                />
              </div>

              <div>
                <Image src="/images/ucla-logo.webp" width={74} height={24} alt="ucla logo" />
              </div>

              <div>
                <Image src="/images/pearson-logo.webp" width={81} height={58} alt="pearson logo" />
              </div>

              <div>
                <Image src="/images/harvard-logo.webp" width={135} height={35} alt="harvard logo" />
              </div>

              <div>
                <Image src="/images/abbott-logo.webp" width={96} height={25} alt="abbot logo" />
              </div>

              <div>
                <Image src="/images/mcgill-logo.webp" width={116} height={26} alt="mcgill logo" />
              </div>
            </Box>
          </Box>
        </Box>
      }
      cancelButton={false}
      actionButton={false}
      onClose={onClose}
    />
  );
}

BlackNovemberLimitModal.propTypes = Props;
